import React, { useState, useEffect, useCallback } from 'react';
import {
  TestTube,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  TrendingUp,
  RefreshCw,
  Download,
  Activity,
  Zap,
  FileText,
  ChevronRight,
} from 'lucide-react';
import { getTestAnalytics, exportTestsToExcel, TestAnalytics } from '@/services/analyticsService';

export const TestCoveragePage: React.FC = () => {
  const [data, setData] = useState<TestAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getTestAnalytics();
      setData(response);
    } catch (err) {
      setError('Erreur lors du chargement des analyses de tests');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = async () => {
    try {
      setExporting(true);
      const blob = await exportTestsToExcel();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `test-analytics-${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${Math.floor(ms / 60000)}m ${Math.floor((ms % 60000) / 1000)}s`;
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'PASSED':
        return 'bg-green-100 text-green-800';
      case 'FAILED':
        return 'bg-red-100 text-red-800';
      case 'RUNNING':
        return 'bg-blue-100 text-blue-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'PASSED':
        return 'Succès';
      case 'FAILED':
        return 'Échec';
      case 'RUNNING':
        return 'En cours';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <XCircle className="text-red-500" size={20} />
          <span className="text-red-700">{error}</span>
          <button
            onClick={fetchData}
            className="ml-auto px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  const latestExec = data?.latestExecution;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tests & Couverture</h1>
          <p className="text-sm text-gray-500 mt-1">
            Suivi des tests et de la qualité du code
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={fetchData}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            Actualiser
          </button>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="flex items-center gap-2 px-4 py-2 text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
          >
            <Download size={16} />
            {exporting ? 'Export...' : 'Exporter Excel'}
          </button>
        </div>
      </div>

      {/* Latest Execution Summary */}
      {latestExec && (
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <TestTube size={20} />
              <h3 className="font-semibold">Dernière exécution</h3>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
              latestExec.status === 'PASSED'
                ? 'bg-green-500/20 text-green-100'
                : 'bg-red-500/20 text-red-100'
            }`}>
              {getStatusLabel(latestExec.status)}
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-purple-200 text-sm">Tests passés</p>
              <p className="text-2xl font-bold">{latestExec.passed}/{latestExec.totalTests}</p>
            </div>
            <div>
              <p className="text-purple-200 text-sm">Taux de succès</p>
              <p className="text-2xl font-bold">
                {((latestExec.passed / latestExec.totalTests) * 100).toFixed(1)}%
              </p>
            </div>
            <div>
              <p className="text-purple-200 text-sm">Couverture</p>
              <p className="text-2xl font-bold">{latestExec.coveragePercent?.toFixed(1) || 0}%</p>
            </div>
            <div>
              <p className="text-purple-200 text-sm">Durée</p>
              <p className="text-2xl font-bold">{formatDuration(latestExec.duration)}</p>
            </div>
          </div>
          <div className="mt-4 text-sm text-purple-200">
            <Clock size={14} className="inline mr-1" />
            {formatDate(latestExec.startedAt)}
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Pass Rate */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Taux de succès global</h3>
            <CheckCircle className="text-green-500" size={20} />
          </div>
          <div className="relative pt-1">
            <div className="flex mb-2 items-center justify-between">
              <span className="text-3xl font-bold text-gray-900">
                {(data?.passRate || 0).toFixed(1)}%
              </span>
            </div>
            <div className="overflow-hidden h-3 text-xs flex rounded-full bg-gray-100">
              <div
                style={{ width: `${data?.passRate || 0}%` }}
                className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-green-500 transition-all duration-500"
              />
            </div>
          </div>
        </div>

        {/* Test Count */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Répartition des tests</h3>
            <Activity className="text-blue-500" size={20} />
          </div>
          {latestExec && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-green-500" />
                  <span className="text-sm text-gray-600">Passés</span>
                </div>
                <span className="font-medium">{latestExec.passed}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-500" />
                  <span className="text-sm text-gray-600">Échoués</span>
                </div>
                <span className="font-medium">{latestExec.failed}</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-gray-400" />
                  <span className="text-sm text-gray-600">Ignorés</span>
                </div>
                <span className="font-medium">{latestExec.skipped}</span>
              </div>
            </div>
          )}
        </div>

        {/* Flaky Tests Warning */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Tests instables</h3>
            <AlertTriangle className="text-yellow-500" size={20} />
          </div>
          {data?.flakyTests && data.flakyTests.length > 0 ? (
            <div className="space-y-2">
              {data.flakyTests.slice(0, 3).map((test) => (
                <div
                  key={test.id}
                  className="flex items-center justify-between p-2 bg-yellow-50 rounded-lg"
                >
                  <span className="text-sm text-gray-700 truncate max-w-[180px]">
                    {test.testName}
                  </span>
                  <span className="text-xs text-yellow-700 font-medium">
                    {test.flakyCount}x
                  </span>
                </div>
              ))}
              {data.flakyTests.length > 3 && (
                <p className="text-sm text-gray-500 text-center">
                  +{data.flakyTests.length - 3} autres
                </p>
              )}
            </div>
          ) : (
            <div className="text-center py-4">
              <Zap className="mx-auto text-green-500 mb-2" size={24} />
              <p className="text-sm text-gray-500">Aucun test instable</p>
            </div>
          )}
        </div>
      </div>

      {/* Coverage Trend */}
      {data?.coverageTrend && data.coverageTrend.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Évolution de la couverture</h3>
            <TrendingUp className="text-indigo-500" size={20} />
          </div>
          <div className="h-48 flex items-end gap-2">
            {data.coverageTrend.map((point, index) => (
              <div
                key={index}
                className="flex-1 flex flex-col items-center"
              >
                <div
                  className="w-full bg-indigo-500 rounded-t transition-all duration-300"
                  style={{ height: `${(point.coverage / 100) * 100}%` }}
                />
                <span className="text-xs text-gray-500 mt-2 rotate-45 origin-left">
                  {new Date(point.date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Executions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100">
          <h3 className="font-semibold text-gray-900">Exécutions récentes</h3>
        </div>
        <div className="divide-y divide-gray-100">
          {data?.recentExecutions && data.recentExecutions.length > 0 ? (
            data.recentExecutions.map((exec) => (
              <div
                key={exec.id}
                className="px-6 py-4 flex items-center justify-between hover:bg-gray-50"
              >
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    exec.status === 'PASSED' ? 'bg-green-100' : 'bg-red-100'
                  }`}>
                    {exec.status === 'PASSED' ? (
                      <CheckCircle className="text-green-600" size={20} />
                    ) : (
                      <XCircle className="text-red-600" size={20} />
                    )}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      #{exec.executionId.slice(0, 8)}
                    </p>
                    <p className="text-sm text-gray-500">
                      {formatDate(exec.startedAt)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <p className="text-sm font-medium text-gray-900">{exec.passed}</p>
                    <p className="text-xs text-gray-500">passés</p>
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-gray-900">{exec.failed}</p>
                    <p className="text-xs text-gray-500">échoués</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(exec.status)}`}>
                    {getStatusLabel(exec.status)}
                  </span>
                  <ChevronRight className="text-gray-400" size={16} />
                </div>
              </div>
            ))
          ) : (
            <div className="px-6 py-12 text-center">
              <FileText className="mx-auto text-gray-400 mb-2" size={24} />
              <p className="text-gray-500">Aucune exécution enregistrée</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TestCoveragePage;
